import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import { t } from '../hooks/useT';

interface Friend {
  friendshipId: string;
  userId: string;
  displayName: string;
  eloRating: number;
}

interface Pending {
  friendshipId: string;
  userId: string;
  displayName: string;
  eloRating?: number;
}

interface SearchResult {
  userId: string;
  displayName: string;
  eloRating: number;
}

export function FriendsPage() {
  const { user } = useAuth();
  const [friends, setFriends] = useState<Friend[]>([]);
  const [pendingIncoming, setPendingIncoming] = useState<Pending[]>([]);
  const [pendingOutgoing, setPendingOutgoing] = useState<Pending[]>([]);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => {
    api<{
      friends: Friend[];
      pendingIncoming: Pending[];
      pendingOutgoing: Pending[];
    }>('/friends')
      .then((data) => {
        setFriends(data.friends);
        setPendingIncoming(data.pendingIncoming);
        setPendingOutgoing(data.pendingOutgoing);
      })
      .catch(() => setError(t('friends.error')));
  };

  useEffect(() => {
    if (user) load();
  }, [user]);

  const handleSearch = async (e: FormEvent) => {
    e.preventDefault();
    if (query.trim().length < 2) return;
    setBusy(true);
    setError('');
    try {
      const data = await api<{ results: SearchResult[] }>(
        `/friends/search?q=${encodeURIComponent(query.trim())}`,
      );
      setResults(data.results);
    } catch {
      setError(t('friends.searchError'));
    } finally {
      setBusy(false);
    }
  };

  const sendRequest = async (userId: string) => {
    setBusy(true);
    try {
      await api('/friends/request', { method: 'POST', body: JSON.stringify({ userId }) });
      setResults([]);
      setQuery('');
      load();
    } catch {
      setError(t('friends.requestError'));
    } finally {
      setBusy(false);
    }
  };

  const accept = async (id: string) => {
    await api(`/friends/${id}/accept`, { method: 'POST' });
    load();
  };

  const reject = async (id: string) => {
    await api(`/friends/${id}/reject`, { method: 'POST' });
    load();
  };

  const remove = async (id: string) => {
    await api(`/friends/${id}`, { method: 'DELETE' });
    load();
  };

  if (!user) {
    return (
      <main className="page friends">
        <div className="profile-card profile-card--empty">
          <p>{t('friends.loginPrompt')}</p>
          <Link to="/login" className="btn primary block">{t('landing.login')}</Link>
        </div>
      </main>
    );
  }

  return (
    <main className="page friends">
      <div className="profile-card">
        <h1>{t('friends.title')}</h1>

        <form className="friends-search" onSubmit={handleSearch}>
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t('friends.searchPlaceholder')}
            minLength={2}
          />
          <button type="submit" className="btn secondary" disabled={busy}>
            {t('friends.search')}
          </button>
        </form>

        {error && <p className="error" role="alert">{error}</p>}

        {results.length > 0 && (
          <ul className="friends-results">
            {results.map((r) => (
              <li key={r.userId}>
                <span>{r.displayName}</span>
                <span className="muted">Elo {r.eloRating}</span>
                <button type="button" className="btn secondary" onClick={() => sendRequest(r.userId)}>
                  {t('friends.add')}
                </button>
              </li>
            ))}
          </ul>
        )}

        {pendingIncoming.length > 0 && (
          <section className="friends-section">
            <h2>{t('friends.incoming')}</h2>
            <ul className="friends-list">
              {pendingIncoming.map((p) => (
                <li key={p.friendshipId}>
                  <span>{p.displayName}</span>
                  <div className="friends-actions">
                    <button type="button" className="btn primary" onClick={() => accept(p.friendshipId)}>
                      {t('friends.accept')}
                    </button>
                    <button type="button" className="btn ghost" onClick={() => reject(p.friendshipId)}>
                      {t('friends.reject')}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {pendingOutgoing.length > 0 && (
          <section className="friends-section">
            <h2>{t('friends.outgoing')}</h2>
            <ul className="friends-list muted">
              {pendingOutgoing.map((p) => (
                <li key={p.friendshipId}>{p.displayName} — {t('friends.pending')}</li>
              ))}
            </ul>
          </section>
        )}

        <section className="friends-section">
          <h2>{t('friends.list')}</h2>
          {friends.length === 0 ? (
            <p className="muted">{t('friends.empty')}</p>
          ) : (
            <ul className="friends-list">
              {friends.map((f) => (
                <li key={f.friendshipId}>
                  <div>
                    <strong>{f.displayName}</strong>
                    <span className="muted"> · Elo {f.eloRating}</span>
                  </div>
                  <div className="friends-actions">
                    {user.isPremium && (
                      <Link to={`/arena?friend=${f.userId}`} className="btn secondary">
                        {t('arena.challenge')}
                      </Link>
                    )}
                    <button type="button" className="btn ghost" onClick={() => remove(f.friendshipId)}>
                      {t('friends.remove')}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </main>
  );
}
